import React, { FunctionComponent } from "react";
import { graphql } from "gatsby";
import { FluidObject } from "gatsby-image";
import { Layout } from "../../components/layout";
import { CoverStory } from "../../components/coverStory";
import { SectionHeader } from "../../components/sectionHeader";
import { PostGrid } from "../../components/postCard";
import { OtherTopics } from "../../components/otherTopics";
import { SEO } from "../../components/seo";
import { PostSnippetNode, toPostSnippet } from "../../data/posts";
import { pluralize } from "../../data/format";
import { TOPIC_ORDER, TOPICS } from "../../topics";

const SECTION_SIZE = 3;

export const pageQuery = graphql`
  {
    cover: allMarkdownRemark(
      limit: 1
      sort: { fields: [frontmatter___publishedDate], order: DESC }
      filter: {
        fields: { collection: { eq: "blog" } }
        frontmatter: { cover: { eq: true } }
      }
    ) {
      nodes {
        ...PostSnippet
        frontmatter {
          coverImg: img {
            childImageSharp {
              fluid(maxWidth: 1400, quality: 90) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
    }
    posts: allMarkdownRemark(
      sort: { fields: [frontmatter___publishedDate], order: DESC }
      filter: { fields: { collection: { eq: "blog" } } }
    ) {
      nodes {
        ...PostSnippet
      }
    }
  }
`;

type CoverNode = PostSnippetNode & {
  frontmatter: { coverImg: { childImageSharp: { fluid: FluidObject } } };
};

interface Home {
  data: {
    cover: { nodes: CoverNode[] };
    posts: { nodes: PostSnippetNode[] };
  };
}

const Home: FunctionComponent<Home> = ({ data }) => {
  const [coverNode] = data.cover.nodes;
  const cover = coverNode && {
    ...toPostSnippet(coverNode),
    img: coverNode.frontmatter.coverImg.childImageSharp.fluid,
  };
  const posts = data.posts.nodes.map(toPostSnippet);

  // Posts are sorted newest first, so the first post's topic leads the page.
  const leadTopic = posts[0]?.topic;
  const leadPosts = posts.filter((post) => post.topic === leadTopic);
  const otherTopics = TOPIC_ORDER.filter((topic) => topic !== leadTopic).map(
    (topic) => ({
      topic,
      latest: posts.find((post) => post.topic === topic),
    })
  );

  return (
    <>
      <SEO title="Home" image="/logo.png" />
      <Layout masthead>
        {cover && <CoverStory {...cover} />}
        {leadTopic && (
          <section className="flex flex-col gap-6 px-gutter-sm pb-6 pt-14 md:px-gutter">
            <SectionHeader
              topic={leadTopic}
              title={TOPICS[leadTopic].name}
              link={{
                href: TOPICS[leadTopic].href,
                label: `All ${pluralize(leadPosts.length, "story", "stories")}`,
              }}
            />
            <PostGrid
              posts={leadPosts
                .filter((post) => post.href !== cover?.href)
                .slice(0, SECTION_SIZE)}
            />
          </section>
        )}
        <OtherTopics topics={otherTopics} />
      </Layout>
    </>
  );
};

export default Home;
